/**
 * Las escenas de los planes de equipo: las mismas pantallas animadas de la landing
 * ("La plataforma ideal para equipos y agencias"), para el panel de fotos del catálogo —
 * ADR-2026-10-27-planes-de-equipo-en-la-pantalla-de-planes-6.
 *
 * COPIADAS de `atlas-landing/landing_page/src/propuestas/final/PantallasEquipos.tsx` y
 * `PlataformaEquipos.tsx` (las piezas de la pantalla de inicio). Lo único que cambia: las rutas de
 * las imágenes (copiadas a `public/images/planes-de-equipo/`), el logo (el de la app) y el prefijo
 * de las animaciones (`pde-`, en `escenasDeEquipo.css`). Si la landing cambia una pantalla o una
 * función, cambia acá.
 */
import React from 'react';
import {
  Archive,
  CalendarDays,
  Check,
  Download,
  Home,
  Images,
  MessageCircle,
  MessagesSquare,
  MousePointer2,
  Play,
  Plus,
  Send,
  Sparkles,
  Upload,
  User,
  Video,
} from 'lucide-react';
import { logoDeBranding } from '../../../branding/aplicar';
import './escenasDeEquipo.css';

const MQ_MENOS = '(prefers-reduced-motion: reduce)';
function useMenosMovimiento() {
  const [menos, setMenos] = React.useState(() => typeof window !== 'undefined' && !!window.matchMedia?.(MQ_MENOS).matches);
  React.useEffect(() => {
    const mq = window.matchMedia?.(MQ_MENOS);
    if (!mq) return;
    const al = () => setMenos(mq.matches);
    mq.addEventListener('change', al);
    return () => mq.removeEventListener('change', al);
  }, []);
  return menos;
}

/*
 * Las pantallas de "La plataforma ideal para equipos y agencias": una por función, todas recreadas
 * (no son capturas) con las mismas piezas que la pantalla de inicio. Comparten las marcas, el equipo y
 * los números: el plan Agencias de 2.000 créditos, con 1.240 usados este mes, que suman igual por marca
 * (Créditos), por persona (Créditos) y respetan los límites de cada uno (Permisos).
 */

const H = "/images/planes-de-equipo/hero";
const M = "/images/planes-de-equipo/moda";
export const L = "/images/planes-de-equipo/plataformas";

export type Marca = { nombre: string; rubro: string; inicial: string; color: string; fotos: [string, string, string, string] };

// Tres workspaces de ejemplo, uno por rubro: lo que cambia al pasar de marca son sus creaciones.
export const MARCAS: Marca[] = [
  {
    nombre: "Nogal Deco",
    rubro: "Hogar y Decoración",
    inicial: "ND",
    color: "#1f2937",
    fotos: [`${H}/lampara-poster.webp`, `${H}/silla_hogar-web.webp`, `${H}/sillon-web.webp`, `${H}/silla-web.webp`],
  },
  {
    nombre: "Trama Moda",
    rubro: "Moda",
    inicial: "TM",
    color: "#9d174d",
    fotos: [`${M}/producciones/pelo-al-sol.webp`, `${M}/producciones/boisere-escarlata.webp`, `${M}/producciones/sillon-azul.webp`, `${M}/producciones/botas-vintage.webp`],
  },
  {
    nombre: "Flora Skin",
    rubro: "Belleza",
    inicial: "FS",
    color: "#047857",
    fotos: [`${H}/crema-poster.webp`, `${H}/serum-poster.webp`, `${H}/shampoo-web.webp`, `${H}/limpiador_facial-web.webp`],
  },
];
const [NOGAL, TRAMA, FLORA] = MARCAS;

type Persona = { nombre: string; inicial: string; color: string; rol: string; marcas: Marca[]; usados: number; limite: number };

// El equipo de la agencia. Usados suman 1.240; los límites, 1.800 de los 2.000 del plan.
const EQUIPO: Persona[] = [
  { nombre: "Lucía Gómez", inicial: "LG", color: "var(--brand-primary)", rol: "Administradora", marcas: [NOGAL, TRAMA, FLORA], usados: 420, limite: 600 },
  { nombre: "Tomás Ríos", inicial: "TR", color: "#ea580c", rol: "Editor", marcas: [NOGAL, FLORA], usados: 310, limite: 400 },
  { nombre: "Sofía Méndez", inicial: "SM", color: "#7c3aed", rol: "Editora", marcas: [TRAMA, FLORA], usados: 380, limite: 500 },
  { nombre: "Martín Bravo", inicial: "MB", color: "#0891b2", rol: "Solo lectura", marcas: [TRAMA], usados: 130, limite: 300 },
];
const [LUCIA, TOMAS, SOFIA, MARTIN] = EQUIPO;

const PLAN = { creditos: 2000, usados: 1240, renuevan: 12 };
// Por marca: también suman 1.240.
const POR_MARCA = [
  { marca: NOGAL, usados: 520 },
  { marca: TRAMA, usados: 430 },
  { marca: FLORA, usados: 290 },
];

const fmt = (n: number) => n.toLocaleString("es-AR");

export function Foto({ src, className = "" }: { src: string; className?: string }) {
  // key = src: al cambiar de marca la foto nueva entra con un fundido.
  return <img key={src} src={src} alt="" loading="lazy" className={`pde-foto h-full w-full object-cover ${className}`} />;
}

/* ── Piezas compartidas ── */

/** El marco de todas las pantallas: mismo fondo, borde y medida (700 × 512 antes de escalar). */
export function Marco({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return <div className={`h-[512px] overflow-hidden rounded-[22px] bg-[#f6f7f9] p-3 text-left text-[#1d1f24] ring-1 ring-black/[0.06] ${className}`}>{children}</div>;
}

function Caja({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return <div className={`rounded-[16px] bg-white p-3.5 ring-1 ring-black/[0.05] ${className}`}>{children}</div>;
}

function Rotulo({ children, extra }: { children: React.ReactNode; extra?: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <p className="m-0 text-[13px] font-bold tracking-[-0.01em]">{children}</p>
      {extra}
    </div>
  );
}

function Chip({ children, tono = "azul" }: { children: React.ReactNode; tono?: "azul" | "verde" | "gris" }) {
  const t = { azul: "bg-[color-mix(in_srgb,var(--brand-primary)_8%,white)] text-brand-primary", verde: "bg-[#e7f6ec] text-[#1c7c3c]", gris: "bg-[#f1f2f5] text-[#5b5f6a]" }[tono];
  return <span className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[10.5px] font-bold ${t}`}>{children}</span>;
}

function Avatar({ p, tam = 28, anillo = false }: { p: Pick<Persona, "inicial" | "color">; tam?: number; anillo?: boolean }) {
  return (
    <span
      className={`grid shrink-0 place-items-center rounded-full font-extrabold text-white ${anillo ? "ring-2 ring-white" : ""}`}
      style={{ background: p.color, width: tam, height: tam, fontSize: tam * 0.36 }}
    >
      {p.inicial}
    </span>
  );
}

function MarcaMini({ m, tam = 22 }: { m: Marca; tam?: number }) {
  return (
    <span className="grid shrink-0 place-items-center rounded-[6px] font-extrabold text-white" style={{ background: m.color, width: tam, height: tam, fontSize: tam * 0.38 }}>
      {m.inicial}
    </span>
  );
}

/** Barra de progreso que se llena al entrar la pantalla. */
function Barra({ valor, total, color = "var(--brand-primary)", alto = 8, retraso = 0 }: { valor: number; total: number; color?: string; alto?: number; retraso?: number }) {
  return (
    <div className="w-full overflow-hidden rounded-full bg-[#eef0f4]" style={{ height: alto }}>
      <div
        className="pde-crece h-full origin-left rounded-full"
        style={{ width: `${(valor / total) * 100}%`, background: color, animationDelay: `${retraso}ms` }}
      />
    </div>
  );
}

/* ── 1. Trabajo colaborativo ── */

function Cursor({ p, className, retraso = 0 }: { p: Persona; className: string; retraso?: number }) {
  return (
    <span className={`pde-cursor absolute flex items-start ${className}`} style={{ animationDelay: `${retraso}ms` }}>
      <MousePointer2 className="h-4 w-4 drop-shadow" style={{ color: p.color, fill: p.color }} aria-hidden="true" />
      <span className="mt-3 rounded-full px-2 py-0.5 text-[10.5px] font-bold text-white shadow" style={{ background: p.color }}>
        {p.nombre.split(" ")[0]}
      </span>
    </span>
  );
}

const ACTIVIDAD = [
  { p: MARTIN, texto: "aprobó 4 fotos de catálogo", hace: "ahora" },
  { p: TOMAS, texto: "generó un video para Reels", hace: "3 min" },
  { p: SOFIA, texto: "comentó en Campaña de verano", hace: "8 min" },
  { p: LUCIA, texto: "subió 6 productos nuevos", hace: "15 min" },
];

export function PantallaColaborativo() {
  const [a, b, c, d] = TRAMA.fotos;
  return (
    <Marco className="flex gap-3">
      <Caja className="flex min-h-0 min-w-0 flex-1 flex-col">
        <Rotulo
          extra={
            <span className="flex items-center gap-2">
              <span className="flex -space-x-1.5">
                {EQUIPO.map((p) => (
                  <Avatar key={p.inicial} p={p} tam={26} anillo />
                ))}
              </span>
              <Chip tono="verde">
                <span className="pde-pulso h-1.5 w-1.5 rounded-full bg-[#22c55e]" /> 4 editando
              </Chip>
            </span>
          }
        >
          Campaña de verano · <span className="text-[#9d174d]">Trama Moda</span>
        </Rotulo>
        <div className="relative mt-3 grid min-h-0 flex-1 grid-cols-[1.25fr_1fr] gap-2.5">
          <div className="relative overflow-hidden rounded-[12px] ring-2 ring-[#7c3aed]">
            <Foto src={a} />
            <span className="absolute left-2 top-2 rounded-full bg-[#7c3aed] px-2 py-0.5 text-[10px] font-bold text-white">Sofía está editando</span>
          </div>
          <div className="grid min-h-0 grid-rows-[1.3fr_1fr] gap-2.5">
            <div className="relative min-h-0 overflow-hidden rounded-[12px]">
              <Foto src={b} />
              <span className="absolute bottom-2 left-2 grid h-6 w-6 place-items-center rounded-full bg-[#22c55e] text-white shadow">
                <Check className="h-3.5 w-3.5" strokeWidth={3} aria-hidden="true" />
              </span>
            </div>
            <div className="grid min-h-0 grid-cols-2 gap-2.5">
              <div className="min-h-0 overflow-hidden rounded-[12px]">
                <Foto src={c} />
              </div>
              <div className="min-h-0 overflow-hidden rounded-[12px]">
                <Foto src={d} />
              </div>
            </div>
          </div>
          {/* Comentario fijado sobre la foto principal. */}
          <div className="pde-aparece absolute bottom-3 left-3 flex max-w-[210px] items-start gap-2 rounded-[12px] bg-white p-2.5 shadow-[0_12px_28px_-12px_rgba(0,0,0,0.45)]" style={{ animationDelay: "350ms" }}>
            <Avatar p={LUCIA} tam={22} />
            <p className="m-0 text-[11px] leading-snug">
              <span className="font-bold">Lucía</span> ¿Probamos esta misma con fondo rojo para el feed?
            </p>
          </div>
          <Cursor p={TOMAS} className="right-[18%] top-[22%]" />
          <Cursor p={SOFIA} className="left-[38%] top-[38%]" retraso={-1600} />
        </div>
      </Caja>
      <div className="flex w-[218px] shrink-0 flex-col gap-3">
        <Caja>
          <Rotulo>En línea</Rotulo>
          <ul className="m-0 mt-2.5 flex list-none flex-col gap-2 p-0">
            {EQUIPO.map((p) => (
              <li key={p.inicial} className="flex items-center gap-2">
                <span className="relative">
                  <Avatar p={p} tam={26} />
                  <span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-[#22c55e] ring-2 ring-white" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[11.5px] font-semibold">{p.nombre}</span>
                  <span className="block truncate text-[10px] text-[#6b7180]">{p.rol}</span>
                </span>
              </li>
            ))}
          </ul>
        </Caja>
        <Caja className="flex-1">
          <Rotulo>Actividad</Rotulo>
          <ul className="m-0 mt-2.5 flex list-none flex-col gap-2.5 p-0">
            {ACTIVIDAD.map(({ p, texto, hace }, i) => (
              <li key={texto} className="pde-aparece flex items-start gap-2" style={{ animationDelay: `${150 + i * 110}ms` }}>
                <Avatar p={p} tam={20} />
                <p className="m-0 flex-1 text-[10.5px] leading-snug text-[#3d414b]">
                  <span className="font-bold text-[#1d1f24]">{p.nombre.split(" ")[0]}</span> {texto}
                  <span className="block text-[9.5px] text-[#9aa0ab]">{hace}</span>
                </p>
              </li>
            ))}
          </ul>
        </Caja>
      </div>
    </Marco>
  );
}

/* ── 3. Account manager dedicado ── */

// Su trabajo es que el equipo aprenda a usar Atlas: capacitaciones y dudas resueltas, no hacer las piezas.
const AM = { nombre: "Carolina Paz", inicial: "CP", color: "#0f766e" };

type Mensaje = { de: Persona | "am"; texto: string; tutorial?: boolean };

const DUDAS: Mensaje[] = [
  { de: TOMAS, texto: "¿Qué formato conviene para un Reel?" },
  { de: "am", texto: "Vertical, 9:16. Lo elegís antes de generar y queda listo para subir." },
  { de: SOFIA, texto: "¿Y cómo armo los posteos del mes para Instagram?" },
  { de: "am", texto: "Desde el calendario de Instagram. Te grabé el paso a paso:", tutorial: true },
  { de: MARTIN, texto: "¡Gracias, Caro! Ya lo vi." },
];

const MODULOS = [
  { texto: "Primeros pasos en Atlas", hecho: true },
  { texto: "Productos y kit de marca", hecho: true },
  { texto: "Fotos para catálogo", hecho: true },
  { texto: "Videos con avatares", hecho: false, proximo: true },
  { texto: "Calendario de Instagram", hecho: false },
];

export function PantallaAccountManager() {
  const hechos = MODULOS.filter((m) => m.hecho).length;
  return (
    <Marco className="flex gap-3">
      <div className="flex w-[236px] shrink-0 flex-col gap-3">
        <Caja className="flex flex-col items-center py-3 text-center">
          <span className="relative">
            <Avatar p={AM} tam={50} />
            <span className="absolute bottom-0 right-0 h-3.5 w-3.5 rounded-full bg-[#22c55e] ring-[3px] ring-white" />
          </span>
          <p className="m-0 mt-2 text-[14px] font-bold">{AM.nombre}</p>
          <p className="m-0 text-[10.5px] text-[#6b7180]">Tu account manager</p>
          <div className="mt-2 grid w-full grid-cols-2 gap-2">
            <span className="flex items-center justify-center gap-1.5 rounded-full bg-[#1d1f24] py-1.5 text-[10.5px] font-bold text-white">
              <MessageCircle className="h-3.5 w-3.5" aria-hidden="true" /> WhatsApp
            </span>
            <span className="flex items-center justify-center gap-1.5 rounded-full py-1.5 text-[10.5px] font-bold ring-1 ring-black/10">
              <CalendarDays className="h-3.5 w-3.5" aria-hidden="true" /> Agendar
            </span>
          </div>
        </Caja>
        <Caja>
          <Rotulo>Próxima capacitación</Rotulo>
          <div className="mt-2.5 flex items-center gap-3">
            <span className="flex w-12 shrink-0 flex-col items-center rounded-[10px] bg-[color-mix(in_srgb,var(--brand-primary)_8%,white)] py-1.5 text-brand-primary">
              <span className="text-[9.5px] font-bold uppercase">Jue</span>
              <span className="text-[19px] font-extrabold leading-none">14</span>
            </span>
            <span className="min-w-0">
              <span className="block text-[12px] font-bold">Videos con avatares</span>
              <span className="block text-[10.5px] text-[#6b7180]">10:00 · En vivo, 45 min</span>
            </span>
          </div>
          <div className="mt-2.5 flex items-center gap-2">
            <span className="flex -space-x-1.5">
              {EQUIPO.map((p) => (
                <Avatar key={p.inicial} p={p} tam={20} anillo />
              ))}
            </span>
            <span className="text-[10px] font-semibold text-[#6b7180]">Todo el equipo invitado</span>
          </div>
        </Caja>
        <Caja className="flex-1">
          <Rotulo extra={<span className="text-[10px] font-bold text-brand-primary">{hechos} de {MODULOS.length}</span>}>Plan de capacitación</Rotulo>
          <div className="mt-2">
            <Barra valor={hechos} total={MODULOS.length} alto={5} />
          </div>
          <ul className="m-0 mt-2.5 flex list-none flex-col gap-[7px] p-0">
            {MODULOS.map(({ texto, hecho, proximo }) => (
              <li key={texto} className="flex items-center gap-2 text-[10.5px]">
                <span className={`grid h-4 w-4 shrink-0 place-items-center rounded-full ${hecho ? "bg-brand-primary text-white" : "ring-[1.5px] ring-brand-primary/40"}`}>
                  {hecho && <Check className="h-2.5 w-2.5" strokeWidth={3.5} aria-hidden="true" />}
                </span>
                <span className={`flex-1 ${hecho ? "text-[#6b7180]" : "font-semibold"}`}>{texto}</span>
                {proximo && <Chip>Jue 14</Chip>}
              </li>
            ))}
          </ul>
        </Caja>
      </div>
      <Caja className="flex min-w-0 flex-1 flex-col">
        <div className="flex items-center gap-2.5 border-b border-black/[0.06] pb-3">
          <span className="flex -space-x-1.5">
            <Avatar p={AM} tam={28} anillo />
            {[TOMAS, SOFIA, MARTIN].map((p) => (
              <Avatar key={p.inicial} p={p} tam={28} anillo />
            ))}
          </span>
          <span className="flex-1">
            <span className="block text-[12.5px] font-bold">Dudas del equipo</span>
            <span className="block text-[10px] text-[#6b7180]">Grupo con {AM.nombre.split(" ")[0]}</span>
          </span>
          <Chip tono="verde">Responde en menos de 2 h</Chip>
        </div>
        <div className="flex flex-1 flex-col justify-end gap-2 py-3">
          {DUDAS.map((m, i) => {
            const am = m.de === "am";
            const quien = am ? AM : (m.de as Persona);
            return (
              <div
                key={m.texto}
                className={`pde-aparece flex items-end gap-2 ${am ? "flex-row-reverse" : ""}`}
                style={{ animationDelay: `${150 + i * 220}ms` }}
              >
                <Avatar p={quien} tam={22} />
                <div className={`flex max-w-[80%] flex-col gap-1.5 ${am ? "items-end" : "items-start"}`}>
                  <p className={`m-0 rounded-[14px] px-3 py-2 text-[11.5px] leading-snug ${am ? "rounded-br-[4px] bg-[#0f766e] text-white" : "rounded-bl-[4px] bg-[#f1f2f5]"}`}>
                    {!am && <span className="mb-0.5 block text-[10px] font-bold" style={{ color: quien.color }}>{(m.de as Persona).nombre.split(" ")[0]}</span>}
                    {m.texto}
                  </p>
                  {m.tutorial && (
                    // El tutorial grabado: un video corto con el paso a paso.
                    <span className="flex items-center gap-2.5 rounded-[14px] bg-[#f1f2f5] p-1.5 pr-3">
                      <span className="relative h-11 w-16 overflow-hidden rounded-[9px]">
                        <Foto src={TRAMA.fotos[0]} />
                        <span className="absolute inset-0 grid place-items-center bg-black/25">
                          <span className="grid h-5 w-5 place-items-center rounded-full bg-white">
                            <Play className="ml-px h-2.5 w-2.5 fill-[#1d1f24] text-[#1d1f24]" aria-hidden="true" />
                          </span>
                        </span>
                      </span>
                      <span>
                        <span className="block text-[11px] font-bold">Planificar el mes en Instagram</span>
                        <span className="block text-[10px] text-[#6b7180]">Tutorial · 3:12</span>
                      </span>
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex items-center gap-2 rounded-full bg-[#f6f7f9] py-1.5 pl-4 pr-1.5 ring-1 ring-black/[0.05]">
          <span className="flex-1 text-[11.5px] text-[#9aa0ab]">Preguntale a {AM.nombre.split(" ")[0]}…</span>
          <span className="grid h-7 w-7 place-items-center rounded-full bg-[#0f766e] text-white">
            <Send className="h-3.5 w-3.5" aria-hidden="true" />
          </span>
        </div>
      </Caja>
    </Marco>
  );
}

/* ── 4. Kit de marca ── */

const COLORES = [
  { hex: "#047857", nombre: "Bosque" },
  { hex: "#A7F3D0", nombre: "Menta" },
  { hex: "#F5EFE6", nombre: "Arena" },
  { hex: "#D4A373", nombre: "Miel" },
  { hex: "#1F2937", nombre: "Carbón" },
];

export function PantallaKitDeMarca() {
  return (
    <Marco className="flex flex-col gap-3">
      <Caja className="flex items-center gap-3 py-3">
        <MarcaMini m={FLORA} tam={30} />
        <span className="flex-1">
          <span className="block text-[14px] font-bold">Kit de marca · Flora Skin</span>
          <span className="block text-[10.5px] text-[#6b7180]">Lo usan todas las personas del equipo en cada creación</span>
        </span>
        <span className="flex -space-x-1.5">
          {EQUIPO.map((p) => (
            <Avatar key={p.inicial} p={p} tam={26} anillo />
          ))}
        </span>
      </Caja>
      <div className="grid grid-cols-[1fr_1.35fr_1fr] gap-3">
        <Caja className="flex flex-col">
          <Rotulo>Logo</Rotulo>
          <div className="mt-2.5 flex flex-1 flex-col items-center justify-center gap-1.5 rounded-[12px] bg-[#f5efe6] py-4">
            <span className="grid h-11 w-11 place-items-center rounded-full bg-[#047857] text-[15px] font-bold italic text-[#f5efe6]" style={{ fontFamily: "Georgia, serif" }}>
              f
            </span>
            <span className="text-[17px] italic tracking-[0.02em] text-[#047857]" style={{ fontFamily: "Georgia, serif" }}>
              Flora Skin
            </span>
          </div>
        </Caja>
        <Caja>
          <Rotulo>Colores</Rotulo>
          <div className="mt-2.5 grid grid-cols-5 gap-1.5">
            {COLORES.map((c, i) => (
              <div key={c.hex} className="pde-aparece flex flex-col gap-1" style={{ animationDelay: `${100 + i * 70}ms` }}>
                <span className="h-14 rounded-[10px] ring-1 ring-black/[0.06]" style={{ background: c.hex }} />
                <span className="text-[9.5px] font-bold leading-none">{c.nombre}</span>
                <span className="text-[8.5px] leading-none text-[#9aa0ab]">{c.hex}</span>
              </div>
            ))}
          </div>
        </Caja>
        <Caja>
          <Rotulo>Tipografías</Rotulo>
          <div className="mt-2.5 flex flex-col gap-2">
            <div className="flex items-center gap-2.5">
              <span className="text-[28px] italic leading-none text-[#047857]" style={{ fontFamily: "Georgia, serif" }}>
                Aa
              </span>
              <span>
                <span className="block text-[11px] font-bold">Serif</span>
                <span className="block text-[9.5px] text-[#6b7180]">Títulos</span>
              </span>
            </div>
            <div className="flex items-center gap-2.5">
              <span className="text-[28px] font-semibold leading-none">Aa</span>
              <span>
                <span className="block text-[11px] font-bold">Sans</span>
                <span className="block text-[9.5px] text-[#6b7180]">Textos</span>
              </span>
            </div>
          </div>
        </Caja>
      </div>
      <Caja className="flex min-h-0 flex-1 flex-col">
        <Rotulo extra={<Chip tono="verde">248 creaciones con el kit</Chip>}>Aplicado en sus creaciones</Rotulo>
        <div className="mt-2.5 grid min-h-0 flex-1 grid-cols-4 gap-2.5">
          {FLORA.fotos.map((f, i) => (
            <div key={f} className="relative overflow-hidden rounded-[12px]">
              <Foto src={f} />
              {/* El sello del kit: logo y franja de color iguales en todas. */}
              <span className="absolute inset-x-0 bottom-0 flex items-center gap-1.5 bg-[linear-gradient(0deg,rgba(4,120,87,0.85),transparent)] px-2 pb-1.5 pt-5">
                <span className="grid h-4 w-4 place-items-center rounded-full bg-[#f5efe6] text-[8px] font-bold italic text-[#047857]" style={{ fontFamily: "Georgia, serif" }}>
                  f
                </span>
                <span className="text-[9.5px] italic text-[#f5efe6]" style={{ fontFamily: "Georgia, serif" }}>
                  {["Hidratá tu piel", "Sérum noche", "Rutina capilar", "Limpieza suave"][i]}
                </span>
              </span>
            </div>
          ))}
        </div>
      </Caja>
    </Marco>
  );
}

/* ── 5. Control de permisos por usuarios ── */

function Interruptor({ on }: { on: boolean }) {
  return (
    <span className={`relative inline-block h-[16px] w-[28px] rounded-full transition-colors ${on ? "bg-brand-primary" : "bg-[#d9dce3]"}`}>
      <span className={`absolute top-[2px] h-3 w-3 rounded-full bg-white shadow transition-all ${on ? "left-[14px]" : "left-[2px]"}`} />
    </span>
  );
}

// Qué puede hacer cada uno: generar, publicar, descargar.
const PERMISOS: Record<string, [boolean, boolean, boolean]> = {
  LG: [true, true, true],
  TR: [true, false, true],
  SM: [true, true, false],
  MB: [false, false, true],
};

export function PantallaPermisos() {
  return (
    <Marco className="flex flex-col gap-3">
      <Caja className="flex min-h-0 flex-1 flex-col">
        <Rotulo
          extra={
            <span className="flex items-center gap-1.5 rounded-full bg-[#1d1f24] px-3 py-1.5 text-[11px] font-bold text-white">
              <Plus className="h-3.5 w-3.5" aria-hidden="true" /> Invitar
            </span>
          }
        >
          Equipo · 4 personas
        </Rotulo>
        <div className="mt-3 grid grid-cols-[1.6fr_0.95fr_0.85fr_repeat(3,0.6fr)_0.9fr] items-center gap-x-2 border-b border-black/[0.06] pb-2 text-[9.5px] font-bold uppercase tracking-[0.06em] text-[#9aa0ab]">
          <span>Persona</span>
          <span>Rol</span>
          <span>Marcas</span>
          <span className="flex items-center justify-center gap-1">
            <Sparkles className="h-3 w-3" aria-hidden="true" /> Generar
          </span>
          <span className="flex items-center justify-center gap-1">
            <Upload className="h-3 w-3" aria-hidden="true" /> Publicar
          </span>
          <span className="flex items-center justify-center gap-1">
            <Download className="h-3 w-3" aria-hidden="true" /> Bajar
          </span>
          <span className="text-right">Límite</span>
        </div>
        <ul className="m-0 flex flex-1 list-none flex-col justify-around p-0">
          {EQUIPO.map((p, i) => (
            <li
              key={p.inicial}
              className={`pde-aparece grid grid-cols-[1.6fr_0.95fr_0.85fr_repeat(3,0.6fr)_0.9fr] items-center gap-x-2 rounded-[12px] px-1 py-2 ${i === 1 ? "bg-[#f5f7ff] ring-1 ring-brand-primary/15" : ""}`}
              style={{ animationDelay: `${100 + i * 90}ms` }}
            >
              <span className="flex min-w-0 items-center gap-2">
                <Avatar p={p} tam={28} />
                <span className="min-w-0">
                  <span className="block truncate text-[11.5px] font-bold">{p.nombre}</span>
                  <span className="block truncate text-[9.5px] text-[#9aa0ab]">{p.nombre.split(" ")[0].toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")}@agencia.com</span>
                </span>
              </span>
              <span>
                <Chip tono={i === 0 ? "azul" : "gris"}>{p.rol}</Chip>
              </span>
              <span className="flex gap-1">
                {p.marcas.map((m) => (
                  <MarcaMini key={m.inicial} m={m} tam={18} />
                ))}
              </span>
              {PERMISOS[p.inicial].map((on, k) => (
                <span key={k} className="flex justify-center">
                  <Interruptor on={on} />
                </span>
              ))}
              <span className="text-right text-[11px] font-bold">
                {fmt(p.limite)} <span className="text-[9.5px] font-semibold text-[#9aa0ab]">/ mes</span>
              </span>
            </li>
          ))}
        </ul>
      </Caja>
      <div className="grid grid-cols-3 gap-3">
        {[
          { titulo: "Roles", texto: "Administración, edición o solo lectura" },
          { titulo: "Marcas por persona", texto: "Cada uno ve solo las marcas que le asignás" },
          { titulo: "Límite de créditos", texto: "Un tope mensual por persona" },
        ].map(({ titulo, texto }) => (
          <Caja key={titulo} className="py-3">
            <p className="m-0 flex items-center gap-1.5 text-[12px] font-bold">
              <span className="grid h-4 w-4 place-items-center rounded-full bg-brand-primary text-white">
                <Check className="h-2.5 w-2.5" strokeWidth={3.5} aria-hidden="true" />
              </span>
              {titulo}
            </p>
            <p className="m-0 mt-1 text-[10.5px] leading-snug text-[#6b7180]">{texto}</p>
          </Caja>
        ))}
      </div>
    </Marco>
  );
}

/* ── 6. Gestión de créditos avanzada ── */

// Uso por semana del mes en curso: suma 1.240.
const SEMANAS = [
  { s: "Sem 1", n: 280 },
  { s: "Sem 2", n: 360 },
  { s: "Sem 3", n: 410 },
  { s: "Sem 4", n: 190 },
];

export function PantallaCreditos() {
  const libre = PLAN.creditos - PLAN.usados;
  const maxSemana = Math.max(...SEMANAS.map((x) => x.n));
  return (
    <Marco className="flex flex-col gap-3">
      <div className="grid grid-cols-3 gap-3">
        <Caja className="py-3">
          <p className="m-0 text-[10.5px] font-semibold text-[#6b7180]">Plan Agencias</p>
          <p className="m-0 mt-1 text-[22px] font-extrabold leading-none tracking-[-0.02em]">
            {fmt(PLAN.creditos)} <span className="text-[11px] font-semibold text-[#6b7180]">créditos / mes</span>
          </p>
          <p className="m-0 mt-1.5 text-[10px] text-[#6b7180]">Compartidos por todo el equipo</p>
        </Caja>
        <Caja className="py-3">
          <p className="m-0 text-[10.5px] font-semibold text-[#6b7180]">Usados este mes</p>
          <p className="m-0 mt-1 text-[22px] font-extrabold leading-none tracking-[-0.02em]">
            {fmt(PLAN.usados)} <span className="text-[11px] font-semibold text-[#6b7180]">· quedan {fmt(libre)}</span>
          </p>
          <div className="mt-2">
            <Barra valor={PLAN.usados} total={PLAN.creditos} alto={6} />
          </div>
        </Caja>
        <Caja className="py-3">
          <p className="m-0 text-[10.5px] font-semibold text-[#6b7180]">Se renuevan en</p>
          <p className="m-0 mt-1 text-[22px] font-extrabold leading-none tracking-[-0.02em]">
            {PLAN.renuevan} <span className="text-[11px] font-semibold text-[#6b7180]">días</span>
          </p>
          <p className="m-0 mt-1.5 flex items-center gap-1 text-[10px] text-[#6b7180]">
            <Video className="h-3 w-3" aria-hidden="true" /> 1 crédito = 1 imagen · 2 = 1 s de video
          </p>
        </Caja>
      </div>
      <div className="grid min-h-0 flex-1 grid-cols-[1.2fr_1fr] gap-3">
        <Caja className="flex flex-col">
          <Rotulo extra={<Chip>Para facturar a cada cliente</Chip>}>Consumo por marca</Rotulo>
          <ul className="m-0 mt-3 flex flex-1 list-none flex-col justify-around p-0">
            {POR_MARCA.map(({ marca, usados }, i) => (
              <li key={marca.inicial} className="flex items-center gap-2.5">
                <MarcaMini m={marca} />
                <span className="w-[74px] shrink-0 truncate text-[11px] font-semibold">{marca.nombre}</span>
                <span className="flex-1">
                  <Barra valor={usados} total={POR_MARCA[0].usados} color={marca.color} alto={10} retraso={120 + i * 100} />
                </span>
                <span className="w-9 shrink-0 text-right text-[11px] font-bold">{fmt(usados)}</span>
              </li>
            ))}
          </ul>
        </Caja>
        <Caja className="flex flex-col">
          <Rotulo>Uso por semana</Rotulo>
          <div className="mt-2 flex flex-1 items-end gap-3 px-1">
            {SEMANAS.map(({ s, n }, i) => (
              <div key={s} className="flex h-full flex-1 flex-col items-center justify-end gap-1">
                <span className="text-[9.5px] font-bold">{n}</span>
                <span
                  className="pde-sube w-full origin-bottom rounded-t-[6px] bg-[linear-gradient(180deg,#3b6bff,var(--brand-primary))]"
                  style={{ height: `${(n / maxSemana) * 120}px`, animationDelay: `${120 + i * 90}ms` }}
                />
                <span className="text-[9px] text-[#9aa0ab]">{s}</span>
              </div>
            ))}
          </div>
        </Caja>
      </div>
      <Caja>
        <Rotulo extra={<Chip tono="gris">Límites por persona</Chip>}>Consumo del equipo</Rotulo>
        <ul className="m-0 mt-3 grid list-none grid-cols-2 gap-x-6 gap-y-3 p-0">
          {EQUIPO.map((p, i) => (
            <li key={p.inicial} className="flex items-center gap-2.5">
              <Avatar p={p} tam={26} />
              <span className="min-w-0 flex-1">
                <span className="flex items-baseline justify-between gap-2">
                  <span className="truncate text-[11.5px] font-semibold">{p.nombre}</span>
                  <span className="shrink-0 text-[10.5px] text-[#6b7180]">
                    <span className="font-bold text-[#1d1f24]">{fmt(p.usados)}</span> / {fmt(p.limite)}
                  </span>
                </span>
                <span className="mt-1.5 block">
                  <Barra valor={p.usados} total={p.limite} color={p.color} alto={6} retraso={200 + i * 90} />
                </span>
              </span>
            </li>
          ))}
        </ul>
      </Caja>
    </Marco>
  );
}

const ANCHO_BASE = 700;
/** Tope para agrandarla cuando la columna es más ancha que el dibujo (escritorio). */
const ESCALA_MAXIMA = 1.3;

/** La interfaz se dibuja a un ancho fijo y se escala entera: en celular se ve igual, más chica. */
function Escalado({ children }: { children: React.ReactNode }) {
  const caja = React.useRef<HTMLDivElement>(null);
  const interior = React.useRef<HTMLDivElement>(null);
  const [medida, setMedida] = React.useState({ escala: 1, alto: 0 });
  React.useLayoutEffect(() => {
    const c = caja.current;
    const i = interior.current;
    if (!c || !i) return;
    const medir = () => {
      const escala = Math.min(ESCALA_MAXIMA, c.clientWidth / ANCHO_BASE);
      setMedida({ escala, alto: i.offsetHeight * escala });
    };
    medir();
    const ro = new ResizeObserver(medir);
    ro.observe(c);
    ro.observe(i);
    return () => ro.disconnect();
  }, []);
  return (
    <div ref={caja} className="relative w-full" style={{ height: medida.alto || undefined }}>
      <div ref={interior} className="absolute left-0 top-0 origin-top-left" style={{ width: ANCHO_BASE, transform: `scale(${medida.escala})` }}>
        {children}
      </div>
    </div>
  );
}

function Insignia({ src }: { src: string }) {
  return (
    <span className="grid h-7 w-7 place-items-center overflow-hidden rounded-[8px] bg-white shadow-[0_4px_10px_-4px_rgba(0,0,0,0.35)] ring-2 ring-white">
      <img src={src} alt="" className="h-full w-full object-contain" />
    </span>
  );
}

/** Una tarjeta del inicio: una foto grande con los destinos encima, el título y una línea. */
function TarjetaDeInicio({ fondo, titulo, texto, foto, logos }: { fondo: string; titulo: string; texto: string; foto: string; logos: string[] }) {
  return (
    <div className="flex flex-col rounded-[16px] p-2.5 ring-1 ring-black/[0.05]" style={{ background: fondo }}>
      <div className="relative aspect-[4/3] overflow-hidden rounded-[11px] bg-white/60">
        <Foto src={foto} />
        <span className="absolute bottom-2 left-2 flex gap-1">
          {logos.map((l) => (
            <Insignia key={l} src={l} />
          ))}
        </span>
      </div>
      <p className="m-0 mt-2.5 px-1 text-[15px] font-bold tracking-[-0.01em] text-[#111318]">{titulo}</p>
      <p className="m-0 mt-0.5 px-1 pb-1 text-[11.5px] leading-snug text-[#5b5f6a]">{texto}</p>
    </div>
  );
}

const NAV = [
  { Icono: Home, label: "Inicio", activo: true },
  { Icono: Images, label: "Creaciones" },
  { Icono: Archive, label: "Mis Productos" },
  { Icono: MessagesSquare, label: "Asistente" },
  { Icono: CalendarDays, label: "Instagram" },
];

function Interfaz({ marca, elegida }: { marca: Marca; elegida: number }) {
  const [a, b, c, d] = marca.fotos;
  return (
    <Marco className="flex gap-3">
      {/* Barra lateral: las marcas de la cuenta a la vista, después los accesos y el plan. */}
      <aside className="flex w-[184px] shrink-0 flex-col rounded-[16px] bg-white p-3.5 ring-1 ring-black/[0.05]">
        <div className="px-1">
          <img src={logoDeBranding('horizontal')} alt="" style={{ height: 15 }} />
        </div>
        <p className="m-0 mt-5 px-1 text-[10px] font-bold uppercase tracking-[0.08em] text-[#9aa0ab]">Tus marcas</p>
        <ul className="m-0 mt-1.5 flex list-none flex-col gap-0.5 p-0">
          {MARCAS.map((m, i) => (
            <li key={m.nombre} className={`flex items-center gap-2 rounded-[10px] px-1.5 py-1.5 transition-colors duration-300 ${i === elegida ? "bg-[color-mix(in_srgb,var(--brand-primary)_8%,white)]" : ""}`}>
              <span className="grid h-7 w-7 shrink-0 place-items-center rounded-[8px] text-[10px] font-extrabold text-white" style={{ background: m.color }}>
                {m.inicial}
              </span>
              <span className="min-w-0 flex-1">
                <span className={`block truncate text-[12px] ${i === elegida ? "font-bold text-brand-primary" : "font-semibold"}`}>{m.nombre}</span>
                <span className="block truncate text-[10px] text-[#6b7180]">{m.rubro}</span>
              </span>
              {i === elegida && <Check className="h-3.5 w-3.5 shrink-0 text-brand-primary" strokeWidth={3} aria-hidden="true" />}
            </li>
          ))}
        </ul>
        <div className="mx-1 my-3 h-px bg-black/[0.07]" />
        <ul className="m-0 flex list-none flex-col gap-0.5 p-0">
          {NAV.map(({ Icono, label, activo }) => (
            <li key={label} className={`flex items-center gap-2.5 rounded-[10px] px-2 py-[7px] text-[12.5px] ${activo ? "bg-[#f3f4f6] font-semibold" : "text-[#3d414b]"}`}>
              <Icono className="h-[15px] w-[15px]" aria-hidden="true" />
              {label}
            </li>
          ))}
        </ul>
        <div className="mt-auto flex items-center gap-2 rounded-[12px] p-2.5 ring-1 ring-black/[0.07]">
          <span className="grid h-7 w-7 place-items-center rounded-full bg-[#f1f2f5]">
            <User className="h-3.5 w-3.5 text-[#6b7180]" aria-hidden="true" />
          </span>
          <span>
            <span className="block text-[11.5px] font-semibold">Plan Agencias</span>
            <span className="block text-[10.5px] font-semibold text-brand-primary">2.000 créditos</span>
          </span>
        </div>
      </aside>

      {/* Inicio de la marca elegida */}
      <div className="flex min-w-0 flex-1 flex-col gap-4 p-2">
        <p key={marca.nombre} className="pde-foto m-0 text-[19px] font-bold tracking-[-0.02em]">
          Hola, <span className="text-brand-primary">{marca.nombre}</span>
        </p>
        <div className="grid grid-cols-3 gap-3">
          <TarjetaDeInicio fondo="#f4eef7" titulo="Redes sociales" texto="Posteos, carruseles y videos." foto={a} logos={[`${L}/instagram.webp`, `${L}/meta.webp`]} />
          <TarjetaDeInicio fondo="#f6f2e6" titulo="Catálogos" texto="Fotos y videos para tu tienda." foto={b} logos={[`${L}/tiendanube.webp`, `${L}/mercado-libre.webp`]} />
          <TarjetaDeInicio fondo="#eef0f4" titulo="Anuncios" texto="Piezas para Meta y Google." foto={c} logos={[`${L}/meta.webp`]} />
        </div>
        <div className="flex flex-1 flex-col">
          <p className="m-0 flex items-baseline gap-3 text-[14px] font-bold">
            Creaciones recientes <span className="text-[11.5px] font-semibold text-brand-primary">Ver todo →</span>
          </p>
          <div className="mt-2.5 grid min-h-[120px] flex-1 grid-cols-4 gap-2.5">
            {[d, c, b, a].map((f, i) => (
              <div key={i} className="overflow-hidden rounded-[12px] bg-[#e9ebef]">
                <Foto src={f} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </Marco>
  );
}

/** La pantalla de cada función: todas con el mismo marco y la misma medida, para que no salte al cambiar. */
function Pantalla({ funcion, marca, elegida }: { funcion: number; marca: Marca; elegida: number }) {
  const pantallas = [
    <PantallaColaborativo key="colaborativo" />,
    <Interfaz key="multimarca" marca={marca} elegida={elegida} />,
    <PantallaAccountManager key="am" />,
    <PantallaKitDeMarca key="kit" />,
    <PantallaPermisos key="permisos" />,
    <PantallaCreditos key="creditos" />,
  ];
  return <Escalado>{pantallas[funcion]}</Escalado>;
}

/** La marca de la pantalla de inicio va pasando sola de una a otra. */
function useMarcaQueRota() {
  const menos = useMenosMovimiento();
  const [elegida, setElegida] = React.useState(0);
  React.useEffect(() => {
    if (menos) return;
    const t = window.setInterval(() => setElegida((i) => (i + 1) % MARCAS.length), 3200);
    return () => window.clearInterval(t);
  }, [menos]);
  return elegida;
}


// Cada función de los planes de equipo, en el orden de la landing.
export const FUNCIONES_DE_EQUIPO = [
  { titulo: "Trabajo colaborativo", texto: "Todo el equipo trabaja en la misma cuenta, sobre las mismas marcas y creaciones." },
  { titulo: "Gestión multimarca", texto: "Todas las marcas que manejás, en una sola cuenta y con un solo plan." },
  { titulo: "Account manager dedicado", texto: "Desde el plan Agencias, una persona de Atlas que capacita a tu equipo y responde sus dudas." },
  { titulo: "Kit de marca", texto: "Logos, colores y estilos que todo el equipo usa igual." },
  { titulo: "Control de permisos por usuarios", texto: "Definí qué puede hacer cada persona del equipo." },
  { titulo: "Gestión de créditos avanzada", texto: "Créditos compartidos para todo el equipo, con límites de consumo por usuario." },
];

/**
 * La pantalla de una función, escalada al ancho que le toque (se dibuja a 700px y se achica o se
 * agranda entera). La de multimarca va pasando de una marca a otra sola.
 */
export function EscenaDeEquipo({ indice }: { indice: number }) {
  const elegida = useMarcaQueRota();
  return (
    <div key={indice} className="pde-pantalla-entra rounded-[22px] bg-white p-1.5 shadow-[0_40px_90px_-40px_rgba(0,0,0,0.6)]">
      <Pantalla funcion={indice} marca={MARCAS[elegida]} elegida={elegida} />
    </div>
  );
}
